import { type ReactNode, type RefObject } from 'react';
import { cn } from 'v5/platform/dom/cn';
import { useFlexOverflow } from './useFlexOverflow';

/** `measure`: an inert copy in the hidden row that sizes the layout. */
export type OverflowItemContext = 'measure' | 'visible';

export interface OverflowContainerItem<T> {
  item: T;
  index: number;
  key: string | number;
}

export interface OverflowItemRenderProps<T> extends OverflowContainerItem<T> {
  context: OverflowItemContext;
  /** Shown out of its order because it is selected and would overflow. */
  isPinned: boolean;
}

export interface OverflowTriggerRenderProps<T> {
  /** `measure`: an inert copy sized like the real trigger. */
  context: 'measure' | 'visible';
  overflowItems: OverflowContainerItem<T>[];
}

export interface UseOverflowContainerOptions<T> {
  items: readonly T[];
  getItemKey: (item: T, index: number) => string | number;
  pinnedIndex?: number;
  /** Shared flex row classes on the container and the hidden measure row. */
  rowClassName?: string;
  containerClassName?: string;
  renderItem: (props: OverflowItemRenderProps<T>) => ReactNode;
  renderTrigger: (props: OverflowTriggerRenderProps<T>) => ReactNode;
}

export interface UseOverflowContainerResult {
  containerProps: {
    ref: RefObject<HTMLDivElement>;
    className: string;
  };
  /** The hidden measure row, the visible items and the trigger. Render it inside `containerProps`. */
  content: ReactNode;
}

/**
 * Headless overflow layout for a horizontal flex row driven by a data array:
 * the items that fit stay in the row, the rest go behind a trigger, which
 * gets them to list. One `renderItem` serves the hidden measure row and the
 * row; the selected item (`pinnedIndex`) never goes behind the trigger.
 */
export function useOverflowContainer<T>({
  items,
  getItemKey,
  pinnedIndex,
  rowClassName = 'flex items-center gap-2',
  containerClassName,
  renderItem,
  renderTrigger
}: UseOverflowContainerOptions<T>): UseOverflowContainerResult {
  const {
    containerRef,
    measureRef,
    visibleIndices,
    overflowIndices,
    pinnedOutOfOrderIndex
  } = useFlexOverflow({
    itemCount: items.length,
    pinnedIndex
  });

  const indexedItems = items.map((item, index) => ({
    item,
    index,
    key: getItemKey(item, index)
  }));
  const overflowItems = overflowIndices.map(index => indexedItems[index]);

  const content = (
    <>
      <div
        ref={measureRef}
        className={cn('absolute invisible', rowClassName)}
        aria-hidden
      >
        {indexedItems.map(entry => (
          <div key={entry.key} className="shrink-0">
            {renderItem({ ...entry, context: 'measure', isPinned: false })}
          </div>
        ))}
        <div className="shrink-0">
          {renderTrigger({ context: 'measure', overflowItems: indexedItems })}
        </div>
      </div>
      {visibleIndices.map(index => (
        <div key={indexedItems[index].key} className="shrink-0">
          {renderItem({
            ...indexedItems[index],
            context: 'visible',
            isPinned: index === pinnedOutOfOrderIndex
          })}
        </div>
      ))}
      {overflowItems.length > 0 && (
        <div className="shrink-0">
          {renderTrigger({ context: 'visible', overflowItems })}
        </div>
      )}
    </>
  );

  return {
    containerProps: {
      ref: containerRef,
      className: cn(
        'relative min-w-0 overflow-clip',
        rowClassName,
        containerClassName
      )
    },
    content
  };
}
